import { useCallback, useEffect, useRef } from 'react'

/**
 * Хук для удержания экрана включённым во время длинных анимаций.
 * Использует Screen Wake Lock API (navigator.wakeLock).
 * Автоматически освобождает wake lock при размонтировании или когда active=false.
 * При возврате на вкладку (visibilitychange) — перевыдаёт блокировку, если она была активна.
 *
 * @param {boolean} active — true = запросить wake lock, false = освободить
 * @returns {{ request: Function, release: Function }}
 */
const useWakeLock = (active = false) => {
  const wakeLockRef = useRef(null)
  const isActiveRef = useRef(active)

  // Запрос wake lock
  const request = useCallback(async () => {
    if (!('wakeLock' in navigator)) return
    try {
      if (wakeLockRef.current) return
      wakeLockRef.current = await navigator.wakeLock.request('screen')
      wakeLockRef.current.addEventListener('release', () => {
        wakeLockRef.current = null
      })
    } catch {
      // Игнорируем ошибки (например, если вкладка неактивна)
    }
  }, [])

  // Освобождение wake lock
  const release = useCallback(() => {
    if (wakeLockRef.current) {
      try {
        wakeLockRef.current.release()
      } catch {
        // Игнорируем
      }
      wakeLockRef.current = null
    }
  }, [])

  // Управление блокировкой по флагу active
  useEffect(() => {
    isActiveRef.current = active
    if (active) {
      request()
    } else {
      release()
    }
  }, [active, request, release])

  // Повторный запрос при возврате на вкладку
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActiveRef.current) {
        request()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [request])

  // Очистка при размонтировании
  useEffect(() => {
    return () => release()
  }, [release])

  return { request, release }
}

export default useWakeLock
